import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { AMC_TERMS_REQUIRED_MESSAGE, AMC_TERMS_REQUIRED_REASON } from '@/api/amcTerms';
import { adsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import {
  AUDIENCE_PERCENT_DEFAULT,
  AUDIENCE_PERCENT_MAX,
  AUDIENCE_PERCENT_MIN,
  clampAudiencePercentage,
  formatAmcAudienceType,
  isReadyAmcAudience,
  readyAmcAudienceId,
} from '@/api/workspaceAudienceApply';
import type { AmcAudience } from '@/types/amcAudiences';
import type { CampaignBudgetAdjustMode } from '@/api/campaignBudgetUpdate';
import {
  CAMPAIGN_BUDGET_ADJUST_MODES,
  campaignBudgetAdjustModeLabel,
} from '@/api/campaignBudgetUpdate';
import {
  WORKSPACE_KEYWORD_BID_MODE_LABELS,
  type WorkspaceKeywordBidAdjustMode,
  type WorkspaceKeywordBidJob,
  type WorkspaceKeywordBidResult,
} from '@/api/workspaceKeywordBidTypes';
import {
  CAMPAIGN_PLACEMENT_FIELDS,
  CAMPAIGN_PLACEMENT_MODE_LABELS,
  emptyCampaignPlacementForm,
  parseCampaignPlacementAdjustments,
  type CampaignPlacementAdjustment,
  type WorkspaceCampaignPlacementJob,
  type WorkspaceCampaignPlacementMode,
  type WorkspaceCampaignPlacementResult,
} from '@/api/workspaceCampaignPlacementTypes';
import {
  groupDemoNegativesByAdGroup,
  type DemoNegativeTarget,
} from '@/api/workspaceBulkActions';
import type {
  WorkspaceSearchTermNegateJob,
  WorkspaceSearchTermNegateMatchType,
  WorkspaceSearchTermNegateResult,
} from '@/api/workspaceSearchTermNegateTypes';

function ModalShell({
  title,
  description,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  description?: string;
  onClose?: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div
        className={cn(
          'relative flex w-full flex-col rounded-xl bg-surface p-5 shadow-xl',
          wide ? 'max-w-xl' : 'max-w-lg'
        )}
      >
        {onClose ? (
          <button
            type="button"
            className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        <h3 className="pr-8 text-base font-semibold text-foreground">{title}</h3>
        {description ? <p className="mt-1 text-sm text-foreground-muted">{description}</p> : null}
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

export function WorkspaceStateChangeModal({
  title,
  entityLabel,
  selectionCount,
  pending,
  initialState = 'PAUSED',
  onCancel,
  onConfirm,
}: {
  title: string;
  entityLabel: string;
  selectionCount: number;
  pending: boolean;
  initialState?: 'ENABLED' | 'PAUSED';
  onCancel: () => void;
  onConfirm: (state: 'ENABLED' | 'PAUSED') => void;
}) {
  const [nextState, setNextState] = useState<'ENABLED' | 'PAUSED'>(initialState);

  return (
    <ModalShell
      title={title}
      description={`Apply a new status to ${selectionCount} selected ${entityLabel}${selectionCount === 1 ? '' : 's'}.`}
    >
      <label htmlFor="workspace-state-action" className="mb-1 block text-xs text-foreground-muted">
        New status
      </label>
      <select
        id="workspace-state-action"
        value={nextState}
        onChange={(event) => setNextState(event.target.value as 'ENABLED' | 'PAUSED')}
        className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
      >
        <option value="ENABLED">Enabled</option>
        <option value="PAUSED">Paused</option>
      </select>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={() => onConfirm(nextState)} disabled={pending}>
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply changes'}
        </Button>
      </div>
    </ModalShell>
  );
}

export function WorkspaceCampaignAudienceModal({
  profileId,
  eligibleCount,
  skippedSdCount,
  pending,
  onCancel,
  onConfirm,
}: {
  profileId: string;
  eligibleCount: number;
  skippedSdCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (audienceId: string, percentage: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [percentage, setPercentage] = useState(String(AUDIENCE_PERCENT_DEFAULT));

  const audiencesQuery = useQuery({
    queryKey: ['amc-audiences', profileId],
    queryFn: async () => {
      const res = await adsApi.getAmcAudiences(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load audiences');
      return res.data.audiences;
    },
  });

  const contextQuery = useQuery({
    queryKey: ['amc-context', profileId],
    queryFn: async () => {
      const res = await adsApi.getAmcContext(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load AMC context');
      return res.data;
    },
    enabled: Boolean(profileId),
  });
  const termsRequired = contextQuery.data?.sync?.reason === AMC_TERMS_REQUIRED_REASON;

  const readyAudiences = useMemo(
    () => (audiencesQuery.data ?? []).filter(isReadyAmcAudience),
    [audiencesQuery.data]
  );

  const filteredAudiences = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return readyAudiences;
    return readyAudiences.filter((row) => {
      const name = (row.audienceName || '').toLowerCase();
      const activation = (row.dspCanonicalId || row.dspAudienceId || '').toLowerCase();
      return name.includes(needle) || activation.includes(needle);
    });
  }, [readyAudiences, search]);

  const numericPercentage = Number(percentage);
  const validPercentage =
    Number.isFinite(numericPercentage) &&
    numericPercentage >= AUDIENCE_PERCENT_MIN &&
    numericPercentage <= AUDIENCE_PERCENT_MAX;
  const canApply = Boolean(selectedId) && validPercentage && eligibleCount > 0 && !pending;

  const skipNote =
    skippedSdCount > 0
      ? ` ${skippedSdCount} Sponsored Display campaign${skippedSdCount === 1 ? '' : 's'} will be skipped.`
      : '';

  return (
    <ModalShell
      wide
      title="Add Audience"
      description={`Apply an AMC audience bid adjustment to ${eligibleCount} selected campaign${eligibleCount === 1 ? '' : 's'}. This replaces any existing audience on each campaign.${skipNote}`}
      onClose={pending ? undefined : onCancel}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="workspace-audience-search" className="mb-1 block text-xs text-foreground-muted">
            Search audiences
          </label>
          <Input
            id="workspace-audience-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or audience ID"
            disabled={pending}
          />
        </div>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
          {audiencesQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading audiences…
            </div>
          ) : audiencesQuery.isError ? (
            <p className="px-3 py-6 text-center text-sm text-error-ink">
              {audiencesQuery.error instanceof Error
                ? audiencesQuery.error.message
                : 'Failed to load audiences'}
            </p>
          ) : readyAudiences.length === 0 && termsRequired ? (
            <p className="px-3 py-6 text-center text-sm text-foreground-muted">{AMC_TERMS_REQUIRED_MESSAGE}</p>
          ) : readyAudiences.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-foreground-muted">
              <p>No activated AMC audiences for this account.</p>
              <Link
                to={`/accounts/${encodeURIComponent(profileId)}/campaigns/audience`}
                className="mt-2 inline-block font-medium text-brand-ink underline underline-offset-2"
              >
                Create an audience
              </Link>
            </div>
          ) : filteredAudiences.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-foreground-muted">
              No audiences match “{search.trim()}”.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {filteredAudiences.map((row: AmcAudience) => {
                const audienceId = readyAmcAudienceId(row) ?? '';
                const inputId = `workspace-audience-${row.audienceExecutionId}`;
                return (
                  <li key={row.audienceExecutionId}>
                    <label
                      htmlFor={inputId}
                      className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-surface-muted"
                    >
                      <input
                        id={inputId}
                        type="radio"
                        name="workspace-audience"
                        className="mt-1"
                        value={audienceId}
                        checked={selectedId === audienceId}
                        onChange={() => setSelectedId(audienceId)}
                        disabled={pending}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">
                          {row.audienceName || 'Untitled audience'}
                        </span>
                        <span className="block text-xs text-foreground-muted">
                          {formatAmcAudienceType(row.audienceType)} · {audienceId}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <label htmlFor="workspace-audience-percentage" className="mb-1 block text-xs text-foreground-muted">
            Bid adjustment %
          </label>
          <Input
            id="workspace-audience-percentage"
            type="number"
            min={AUDIENCE_PERCENT_MIN}
            max={AUDIENCE_PERCENT_MAX}
            step="1"
            value={percentage}
            onChange={(event) => setPercentage(event.target.value)}
            disabled={pending}
          />
          <p className="mt-1 text-xs text-foreground-muted">
            Integer from {AUDIENCE_PERCENT_MIN} to {AUDIENCE_PERCENT_MAX}. Default is {AUDIENCE_PERCENT_DEFAULT}.
          </p>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(selectedId, clampAudiencePercentage(numericPercentage))}
          disabled={!canApply}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
        </Button>
      </div>
    </ModalShell>
  );
}

export function WorkspaceCampaignBudgetModal({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  onCancel: () => void;
  onConfirm: (mode: CampaignBudgetAdjustMode, value: number) => void;
}) {
  const [mode, setMode] = useState<CampaignBudgetAdjustMode>('INCREASE_AMOUNT');
  const [value, setValue] = useState('');
  const numeric = Number(value);
  const valid = Number.isFinite(numeric) && numeric > 0;

  return (
    <ModalShell
      title="Change Budget"
      description="Choose how to adjust the budget for the selected campaigns."
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="workspace-budget-mode" className="mb-1 block text-xs text-foreground-muted">
            Budget adjustment
          </label>
          <select
            id="workspace-budget-mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as CampaignBudgetAdjustMode)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            {CAMPAIGN_BUDGET_ADJUST_MODES.map((option) => (
                <option key={option} value={option}>
                  {campaignBudgetAdjustModeLabel(option)}
                </option>
              )
            )}
          </select>
        </div>
        <div>
          <label htmlFor="workspace-budget-value" className="mb-1 block text-xs text-foreground-muted">
            Value
          </label>
          <Input
            id="workspace-budget-value"
            type="number"
            min="0"
            step={mode.endsWith('PERCENT') ? '1' : '0.01'}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={mode.endsWith('PERCENT') ? 'Percent' : 'Amount'}
          />
          {mode === 'DECREASE_PERCENT' ? (
            <p className="mt-1 text-xs text-foreground-muted">Decrease percentage cannot exceed 100.</p>
          ) : null}
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(mode, numeric)}
          disabled={pending || !valid}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Review changes'}
        </Button>
      </div>
    </ModalShell>
  );
}

export function WorkspaceKeywordBidModal({
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (mode: WorkspaceKeywordBidAdjustMode, value: number) => void;
}) {
  const [mode, setMode] = useState<WorkspaceKeywordBidAdjustMode>('SET');
  const [value, setValue] = useState('');
  const numeric = Number(value);
  const valid = Number.isFinite(numeric) && numeric > 0;
  const decreaseTooHigh = mode === 'DECREASE_PERCENT' && numeric > 100;

  return (
    <ModalShell
      title="Change Bid"
      description={`Adjust bids for ${selectionCount} selected keyword${selectionCount === 1 ? '' : 's'}.`}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="workspace-bid-mode" className="mb-1 block text-xs text-foreground-muted">
            Bid adjustment
          </label>
          <select
            id="workspace-bid-mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as WorkspaceKeywordBidAdjustMode)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            {(Object.keys(WORKSPACE_KEYWORD_BID_MODE_LABELS) as WorkspaceKeywordBidAdjustMode[]).map(
              (option) => (
                <option key={option} value={option}>
                  {WORKSPACE_KEYWORD_BID_MODE_LABELS[option]}
                </option>
              )
            )}
          </select>
        </div>
        <div>
          <label htmlFor="workspace-bid-value" className="mb-1 block text-xs text-foreground-muted">
            Value
          </label>
          <Input
            id="workspace-bid-value"
            type="number"
            min="0"
            step={mode.endsWith('PERCENT') ? '1' : '0.01'}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={mode.endsWith('PERCENT') ? 'Percent' : 'Bid amount'}
          />
          {mode === 'DECREASE_PERCENT' ? (
            <p className="mt-1 text-xs text-foreground-muted">Decrease percentage cannot exceed 100.</p>
          ) : null}
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(mode, numeric)}
          disabled={pending || !valid || decreaseTooHigh || selectionCount <= 0}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply bid change'}
        </Button>
      </div>
    </ModalShell>
  );
}

export function WorkspaceSuggestedBidModal({
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell
      title="Set to suggested bid"
      description={`Set the bid of ${selectionCount} selected keyword${
        selectionCount === 1 ? '' : 's'
      } to Amazon's suggested bid. Keywords with no suggestion, or already at that bid, are skipped.`}
    >
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={pending || selectionCount <= 0}>
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply suggested bids'}
        </Button>
      </div>
    </ModalShell>
  );
}

export function WorkspaceKeywordBidProgressModal({
  job,
  failedResults,
  isRunning,
  onClose,
  onRetryFailed,
}: {
  job: WorkspaceKeywordBidJob;
  failedResults: WorkspaceKeywordBidResult[];
  isRunning: boolean;
  onClose: () => void;
  onRetryFailed: () => void;
}) {
  const total = job.applyProgress.total ?? job.totals.total ?? 0;
  const processed = job.applyProgress.processed ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : isRunning ? 0 : 100;
  const failed = job.totals.failed ?? 0;
  const applied = job.totals.applied ?? 0;
  const skipped = job.totals.skipped ?? 0;
  const terminal = !isRunning;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">
          {isRunning ? 'Applying bid changes' : 'Bid change results'}
        </h3>
        <p className="mt-1 text-sm text-foreground-muted">
          {isRunning
            ? `Updating keywords… ${processed} of ${total || '—'}`
            : job.status === 'failed'
              ? job.applyProgress.error || 'Bid change job failed'
              : `Applied ${applied}, skipped ${skipped}, failed ${failed}`}
        </p>

        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full bg-foreground transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-foreground-muted">{pct}%</p>
        </div>

        {terminal && failed > 0 ? (
          <div className="app-table-scroll mt-4 max-h-56 min-w-0 max-w-full overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-surface-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Keyword</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {failedResults.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 text-foreground-muted" colSpan={2}>
                      {failed} keyword{failed === 1 ? '' : 's'} failed.
                    </td>
                  </tr>
                ) : (
                  failedResults.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 text-foreground">
                        {row.keywordText || row.keywordId}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {row.errorMessage || row.errorCode || 'Failed'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          {terminal && failed > 0 ? (
            <Button onClick={onRetryFailed} disabled={isRunning}>
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry failed'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={isRunning}>
            {terminal ? 'Close' : 'Running…'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WorkspaceCampaignPlacementModal({
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (adjustments: CampaignPlacementAdjustment[]) => void;
}) {
  const [form, setForm] = useState(emptyCampaignPlacementForm);
  const parsed = parseCampaignPlacementAdjustments(form);
  const noun = selectionCount === 1 ? 'Campaign' : 'Campaigns';

  return (
    <ModalShell
      title={`Change Placement Bidding for ${selectionCount} ${noun}`}
      description="Adjust placement bidding for selected campaigns."
      onClose={pending ? undefined : onCancel}
    >
      <div className="space-y-3">
        {CAMPAIGN_PLACEMENT_FIELDS.map((field) => (
          <div key={field.id} className="grid grid-cols-[minmax(0,1fr)_9rem_5.5rem] items-center gap-2">
            <label htmlFor={`workspace-placement-${field.id}-mode`} className="text-sm font-medium text-foreground">
              {field.label}
            </label>
            <Select
              id={`workspace-placement-${field.id}-mode`}
              className="w-full"
              value={form[field.id].mode}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  [field.id]: {
                    ...prev[field.id],
                    mode: event.target.value as WorkspaceCampaignPlacementMode | '',
                  },
                }))
              }
              aria-label={`${field.label} adjustment`}
            >
              <option value=""> </option>
              {(Object.keys(CAMPAIGN_PLACEMENT_MODE_LABELS) as WorkspaceCampaignPlacementMode[]).map(
                (option) => (
                  <option key={option} value={option}>
                    {CAMPAIGN_PLACEMENT_MODE_LABELS[option]}
                  </option>
                )
              )}
            </Select>
            <Input
              id={`workspace-placement-${field.id}-value`}
              className="w-full"
              type="number"
              value={form[field.id].value}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  [field.id]: { ...prev[field.id], value: event.target.value },
                }))
              }
              aria-label={`${field.label} value`}
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (parsed.error || parsed.adjustments.length === 0) return;
            onConfirm(parsed.adjustments);
          }}
          disabled={pending || Boolean(parsed.error)}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply Changes'}
        </Button>
      </div>
    </ModalShell>
  );
}

export function WorkspaceCampaignPlacementProgressModal({
  job,
  failedResults,
  isRunning,
  onClose,
  onRetryFailed,
}: {
  job: WorkspaceCampaignPlacementJob;
  failedResults: WorkspaceCampaignPlacementResult[];
  isRunning: boolean;
  onClose: () => void;
  onRetryFailed: () => void;
}) {
  const total = job.applyProgress.total ?? job.totals.total ?? 0;
  const processed = job.applyProgress.processed ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : isRunning ? 0 : 100;
  const failed = job.totals.failed ?? 0;
  const applied = job.totals.applied ?? 0;
  const skipped = job.totals.skipped ?? 0;
  const terminal = !isRunning;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">
          {isRunning ? 'Working on it' : 'Placement bidding results'}
        </h3>
        <p className="mt-1 text-sm text-foreground-muted">
          {isRunning
            ? `Updating campaigns… ${processed} of ${total || '—'}`
            : job.status === 'failed'
              ? job.applyProgress.error || 'Placement bidding job failed'
              : `Applied ${applied}, skipped ${skipped}, failed ${failed}`}
        </p>

        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full bg-foreground transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-foreground-muted">{pct}%</p>
        </div>

        {terminal && failed > 0 ? (
          <div className="app-table-scroll mt-4 max-h-56 min-w-0 max-w-full overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-surface-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Campaign</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {failedResults.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 text-foreground-muted" colSpan={2}>
                      {failed} campaign{failed === 1 ? '' : 's'} failed.
                    </td>
                  </tr>
                ) : (
                  failedResults.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 text-foreground">{row.campaignName || row.campaignId}</td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {row.errorMessage || row.errorCode || 'Failed'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          {terminal && failed > 0 ? (
            <Button onClick={onRetryFailed} disabled={isRunning}>
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry failed'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={isRunning}>
            {terminal ? 'Close' : 'Running…'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WorkspaceSearchTermNegateModal({
  targets,
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  targets: DemoNegativeTarget[];
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (matchType: WorkspaceSearchTermNegateMatchType) => void;
}) {
  const [matchType, setMatchType] = useState<WorkspaceSearchTermNegateMatchType>('NEGATIVE_EXACT');
  const groups = useMemo(() => groupDemoNegativesByAdGroup(targets), [targets]);
  const termCount = groups.reduce((sum, group) => sum + group.terms.length, 0);
  const displayCount = selectionCount > 0 ? selectionCount : termCount;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-2xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">Add to Negative Keywords</h3>
        <p className="mt-1 text-sm text-foreground-muted">
          Add {displayCount} term{displayCount === 1 ? '' : 's'} as negatives
          {groups.length > 0
            ? ` across ${groups.length} ad group${groups.length === 1 ? '' : 's'}`
            : ''}
          .
        </p>
        <div className="mt-4">
          {groups.length > 0 ? (
            <div className="mb-3 max-h-64 space-y-3 overflow-y-auto rounded-lg border border-border p-3">
              {groups.map((group) => (
                <div key={`${group.campaignId}|${group.adGroupId}`}>
                  <p className="text-sm font-medium text-foreground">
                    {group.campaignName}
                    <span className="font-normal text-foreground-muted"> · {group.adGroupName}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-muted">
                    {group.terms.length} term{group.terms.length === 1 ? '' : 's'}
                  </p>
                  <ul className="mt-1 list-inside list-disc text-sm text-foreground">
                    {group.terms.slice(0, 8).map((term) => (
                      <li key={term}>{term}</li>
                    ))}
                    {group.terms.length > 8 ? (
                      <li className="text-foreground-muted">+{group.terms.length - 8} more</li>
                    ) : null}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="mb-3 text-sm text-foreground-muted">
              {displayCount} matching search term{displayCount === 1 ? '' : 's'} will be added as
              negatives.
            </p>
          )}

          <label htmlFor="workspace-negate-match" className="mb-1 block text-xs text-foreground-muted">
            Negative match type
          </label>
          <select
            id="workspace-negate-match"
            value={matchType}
            onChange={(event) =>
              setMatchType(event.target.value as WorkspaceSearchTermNegateMatchType)
            }
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            <option value="NEGATIVE_EXACT">Negative exact</option>
            <option value="NEGATIVE_PHRASE">Negative phrase</option>
          </select>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={pending}>
              Cancel
            </Button>
            <Button
              onClick={() => onConfirm(matchType)}
              disabled={pending || displayCount <= 0}
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add negatives'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WorkspaceSearchTermNegateProgressModal({
  job,
  failedResults,
  isRunning,
  onClose,
  onRunInBackground,
  onRetryFailed,
}: {
  job: WorkspaceSearchTermNegateJob | null;
  failedResults: WorkspaceSearchTermNegateResult[];
  isRunning: boolean;
  onClose: () => void;
  onRunInBackground?: () => void;
  onRetryFailed: () => void;
}) {
  const total = job?.applyProgress.total ?? job?.totals.total ?? 0;
  const processed = job?.applyProgress.processed ?? 0;
  const pct =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : isRunning ? 0 : 100;
  const failed = job?.totals.failed ?? 0;
  const applied = job?.totals.applied ?? 0;
  const skipped = job?.totals.skipped ?? 0;
  const terminal = !isRunning;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">
          {isRunning
            ? job
              ? 'Adding negative keywords'
              : 'Starting…'
            : 'Negative keyword results'}
        </h3>
        <p className="mt-1 text-sm text-foreground-muted">
          {isRunning
            ? job
              ? `Creating negatives… ${processed} of ${total || '—'}`
              : 'Preparing negate job…'
            : job?.status === 'failed'
              ? job.applyProgress.error || 'Negate job failed'
              : `Applied ${applied}, skipped ${skipped}, failed ${failed}`}
        </p>

        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full bg-foreground transition-all duration-300"
              style={{ width: `${isRunning && !job ? 15 : pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-foreground-muted">
            {isRunning && !job ? 'Starting' : `${pct}%`}
          </p>
        </div>

        {terminal && failed > 0 ? (
          <div className="app-table-scroll mt-4 max-h-56 min-w-0 max-w-full overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-surface-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Search term</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {failedResults.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 text-foreground-muted" colSpan={2}>
                      {failed} search term{failed === 1 ? '' : 's'} failed.
                    </td>
                  </tr>
                ) : (
                  failedResults.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 text-foreground">
                        {row.searchTerm || row.searchTermKey}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {row.errorMessage || row.errorCode || 'Failed'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          {terminal && failed > 0 ? (
            <Button onClick={onRetryFailed} disabled={isRunning}>
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry failed'}
            </Button>
          ) : null}
          {isRunning && onRunInBackground ? (
            <Button variant="secondary" onClick={onRunInBackground}>
              Run in background
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose} disabled={isRunning}>
              {terminal ? 'Close' : 'Running…'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function WorkspaceSearchTermNegateProgressBanner({
  job,
  isRunning,
  onOpen,
  onRetryFailed,
  onDismiss,
}: {
  job: WorkspaceSearchTermNegateJob | null;
  isRunning: boolean;
  onOpen: () => void;
  onRetryFailed?: () => void;
  onDismiss?: () => void;
}) {
  if (!isRunning && !job) return null;
  const total = job?.applyProgress.total ?? job?.totals.total ?? 0;
  const processed = job?.applyProgress.processed ?? 0;
  const failed = job?.totals.failed ?? 0;
  const canRetry = !isRunning && failed > 0 && onRetryFailed;
  const label = isRunning
    ? job
      ? `Adding negative keywords — ${processed} of ${total || '—'}`
      : 'Starting negative keyword job…'
    : failed > 0
      ? `Negative keywords failed — ${failed}`
      : 'Negative keyword job finished';

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-muted px-3 py-2 text-left text-sm text-foreground">
      <span className="inline-flex items-center gap-2">
        {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {label}
      </span>
      <span className="inline-flex items-center gap-3">
        {canRetry ? (
          <button
            type="button"
            className="font-medium underline underline-offset-2"
            onClick={onRetryFailed}
          >
            Retry failed
          </button>
        ) : null}
        <button
          type="button"
          className="font-medium underline underline-offset-2"
          onClick={onOpen}
        >
          View
        </button>
        {canRetry && onDismiss ? (
          <button
            type="button"
            className="font-medium text-foreground-muted underline underline-offset-2"
            onClick={onDismiss}
          >
            Dismiss
          </button>
        ) : null}
      </span>
    </div>
  );
}
